import {useMemo} from 'react'

import type {DateValue} from '../../../../api/columns/contracts/date'
import type {Iteration} from '../../../../api/columns/contracts/iteration'
import {MemexColumnDataType, SystemColumnId} from '../../../../api/columns/contracts/memex-column'
import type {NumericValue} from '../../../../api/columns/contracts/number'
import type {SingleSelectValue} from '../../../../api/columns/contracts/single-select'
import type {EnrichedText} from '../../../../api/columns/contracts/text'
import type {TitleValueWithContentType} from '../../../../api/columns/contracts/title'
import type {
  ExtendedRepository,
  IssueType,
  Label,
  LinkedPullRequest,
  Milestone,
  ParentIssue,
  SubIssuesProgress,
} from '../../../../api/common-contracts'
import {ItemType} from '../../../../api/memex-items/item-type'
import {itemFromAssignee, itemFromReview, type UserGroupItem} from '../../../../components/table/cells/user-group'
import {
  AssigneesEditor,
  IssueTypeEditor,
  LabelsEditor,
  MilestoneEditor,
  NumberEditor,
  ParentIssueEditor,
  TextEditor,
  TitleEditor,
} from '../../../../components/table/editors'
import {DateEditor} from '../../../../components/table/editors/date-editor'
import {IterationEditor} from '../../../../components/table/editors/iteration-editor'
import {RepositoryEditor} from '../../../../components/table/editors/repository-editor'
import {SingleSelectEditor} from '../../../../components/table/editors/single-select-editor'
import {
  AssigneesRenderer,
  DateRenderer,
  IssueTypeRenderer,
  LabelsRenderer,
  LoadingAssigneesCell,
  LoadingDateCell,
  LoadingIssueTypeCell,
  LoadingLabelsCell,
  LoadingMilestoneCell,
  LoadingNumberCell,
  LoadingRepositoryCell,
  LoadingSingleSelectCell,
  LoadingTextCell,
  LoadingTitleCell,
  MilestoneRenderer,
  NumberRenderer,
  RepositoryRenderer,
  SingleSelectRenderer,
  TextRenderer,
  TitleRenderer,
} from '../../../../components/table/renderers'
import {IterationRenderer, LoadingIterationCell} from '../../../../components/table/renderers/iteration-renderer'
import {
  LinkedPullRequestsRenderer,
  LoadingLinkedPullRequestsCell,
} from '../../../../components/table/renderers/linked-pull-requests-renderer'
import {LoadingParentIssueCell, ParentIssueRenderer} from '../../../../components/table/renderers/parent-issue-renderer'
import {LoadingReviewersCell, ReviewersRenderer} from '../../../../components/table/renderers/reviewers-renderer'
import {
  LoadingSubIssuesProgressCell,
  SubIssuesProgressRenderer,
} from '../../../../components/table/renderers/sub-issues-progress-renderer'
import type {CustomColumnMeta} from '../../../../components/table/state-providers/table-columns/types'
import {getAllIterations} from '../../../../helpers/iterations'
import {
  asCustomDateValue,
  asCustomNumberValue,
  asCustomTextValue,
  asSingleSelectValue,
} from '../../../../helpers/parsing'
import type {ColumnModel} from '../../../../models/column-model'
import {isIterationColumnModel} from '../../../../models/column-model/guards'
import {type ColumnValue, EmptyValue, LoadingValue, withValue} from '../../../../models/column-value'
import {itemHasColumnLoaded} from '../../../../state-providers/memex-items/memex-items-data'
import {Resources} from '../../../../strings'

type PartialCustomColumnMeta = Pick<CustomColumnMeta, 'Cell' | 'CellEditor' | 'Placeholder'> & {
  typeToEditEnabled?: boolean
  nonNavigable?: boolean
  /**
   * Unless 'nullWhenRedacted' is explicitly configured as false,
   * the Cell renderer will return null for redacted items.
   */
  nullWhenRedacted?: boolean
}
type PartialCustomColumnMetaLookup = {[P in MemexColumnDataType]: PartialCustomColumnMeta}

const columnMetaByFieldDataType: PartialCustomColumnMetaLookup = {
  title: {
    nullWhenRedacted: false,
    typeToEditEnabled: false,
    Placeholder: <LoadingTitleCell />,
    Cell(props) {
      const columnId = SystemColumnId.Title

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<TitleValueWithContentType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <TitleRenderer
          currentValue={currentValue}
          model={props.row.original}
          isDisabled={props.isDisabled}
          depth={props.row.depth}
          expandRef={props.expandRef}
          expandable={props.row.getCanExpand()}
          expanded={props.isExpanded}
          rowId={props.row.id}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original
      const {replaceContents} = props

      const columnId = SystemColumnId.Title

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<TitleValueWithContentType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <TitleEditor model={item} replaceContents={replaceContents} currentValue={currentValue} />
    },
  },
  text: {
    Placeholder: <LoadingTextCell />,
    Cell(props) {
      const columnId = props.column.columnModel?.id || -1

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<EnrichedText>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomTextValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <TextRenderer currentValue={currentValue} model={props.row.original} isDisabled={props.isDisabled} />
    },
    CellEditor(props) {
      const item = props.row.original

      if (!props.column.columnModel || !props.column.columnModel.userDefined) {
        throw Error(`${Resources.textEditor} ${Resources.requiresCustomColumn}`)
      }

      const {replaceContents} = props

      const columnId = props.column.columnModel.id

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<EnrichedText>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomTextValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <TextEditor
          currentValue={currentValue}
          model={item}
          replaceContents={replaceContents}
          columnId={columnId}
          rowId={props.row.id}
          isIssueField={props.column.columnModel.isIssueField}
        />
      )
    },
  },
  number: {
    Placeholder: <LoadingNumberCell />,
    Cell(props) {
      const columnId = props.column.columnModel?.id || -1

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<NumericValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomNumberValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <NumberRenderer currentValue={currentValue} model={props.row.original} isDisabled={props.isDisabled} />
    },
    CellEditor(props) {
      const item = props.row.original
      const {replaceContents} = props

      if (!props.column.columnModel || !props.column.columnModel.userDefined) {
        throw Error(`${Resources.numberEditor} ${Resources.requiresCustomColumn}`)
      }

      const columnId = props.column.columnModel.id

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<NumericValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomNumberValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <NumberEditor
          currentValue={currentValue}
          model={item}
          replaceContents={replaceContents}
          rowId={props.row.id}
          columnId={columnId}
          isIssueField={props.column.columnModel.isIssueField}
        />
      )
    },
  },
  singleSelect: {
    Placeholder: <LoadingSingleSelectCell />,
    Cell(props) {
      const columnId = props.column.columnModel?.id || -1
      const columnData = props.row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)
      const currentValue = useMemo<ColumnValue<SingleSelectValue>>(() => {
        if (!isLoaded) return LoadingValue

        const value = asSingleSelectValue(columnData)
        if (value) return withValue(value)

        return EmptyValue
      }, [columnData, isLoaded])

      if (props.column.columnModel?.dataType !== MemexColumnDataType.SingleSelect) {
        throw new Error(Resources.singleSelectCustomModelError)
      }

      return (
        <SingleSelectRenderer
          currentValue={currentValue}
          model={props.row.original}
          options={props.column.columnModel.settings.options}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original
      const columnId = props.column.columnModel?.id || -1
      const columnData = props.row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)
      const currentValue = useMemo<ColumnValue<SingleSelectValue>>(() => {
        if (!isLoaded) return LoadingValue

        const value = asSingleSelectValue(columnData)
        if (value) return withValue(value)

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) return null

      if (props.column.columnModel?.dataType !== MemexColumnDataType.SingleSelect) {
        throw Error(Resources.singleSelectCustomModelError)
      }

      return (
        <SingleSelectEditor
          currentValue={currentValue}
          model={item}
          rowIndex={props.row.index}
          columnModel={props.column.columnModel}
          columnId={columnId}
        />
      )
    },
  },
  date: {
    Placeholder: <LoadingDateCell />,
    Cell(props) {
      const columnId = props.column.columnModel?.id || -1

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<DateValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomDateValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <DateRenderer currentValue={currentValue} model={props.row.original} isDisabled={props.isDisabled} />
    },
    CellEditor(props) {
      const item = props.row.original
      const {replaceContents} = props

      if (!props.column.columnModel || !props.column.columnModel.userDefined) {
        throw Error(`${Resources.dateEditor} ${Resources.requiresCustomColumn}`)
      }

      const columnId = props.column.columnModel.id

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<DateValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomDateValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <DateEditor
          currentValue={currentValue}
          model={item}
          replaceContents={replaceContents}
          rowIndex={props.row.index}
          columnId={props.column.columnModel.id}
          isIssueField={props.column.columnModel.isIssueField}
        />
      )
    },
  },
  assignees: {
    Placeholder: <LoadingAssigneesCell />,
    Cell(props) {
      const columnId = SystemColumnId.Assignees

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<UserGroupItem>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData.map(itemFromAssignee))
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <AssigneesRenderer
          currentValue={currentValue}
          model={props.row.original}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original

      const columnId = SystemColumnId.Assignees

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<UserGroupItem>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData.map(itemFromAssignee))
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      return <AssigneesEditor currentValue={currentValue} model={item} rowIndex={props.row.index} columnId={columnId} />
    },
  },
  iteration: {
    Placeholder: <LoadingIterationCell />,
    Cell(props) {
      const columnId = props.column.columnModel?.id || -1

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<Iteration>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asSingleSelectValue(columnData)

        if (!value) {
          return EmptyValue
        }

        const iterations = isIterationColumnModel(props.column.columnModel)
          ? getAllIterations(props.column.columnModel)
          : []
        const matchingOption = iterations.find(o => o.id === value.id)

        if (matchingOption) {
          return withValue(matchingOption)
        }

        return EmptyValue
      }, [columnData, isLoaded, props.column.columnModel])

      return (
        <IterationRenderer
          currentValue={currentValue}
          model={props.row.original}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original

      const columnId = props.column.columnModel?.id || -1

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<Iteration>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asSingleSelectValue(columnData)

        if (!value) {
          return EmptyValue
        }

        const iterations = isIterationColumnModel(props.column.columnModel)
          ? getAllIterations(props.column.columnModel)
          : []
        const matchingOption = iterations.find(o => o.id === value.id)

        if (matchingOption) {
          return withValue(matchingOption)
        }

        return EmptyValue
      }, [columnData, isLoaded, props.column.columnModel])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      if (props.column.columnModel?.dataType !== MemexColumnDataType.Iteration) {
        throw Error(Resources.iterationCustomModelError)
      }

      return (
        <IterationEditor
          currentValue={currentValue}
          model={item}
          rowIndex={props.row.index}
          columnModel={props.column.columnModel}
          columnId={columnId}
        />
      )
    },
  },
  milestone: {
    Placeholder: <LoadingMilestoneCell />,
    Cell(props) {
      const columnId = SystemColumnId.Milestone

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<Milestone>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <MilestoneRenderer
          currentValue={currentValue}
          model={props.row.original}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original

      const columnId = SystemColumnId.Milestone

      const columnData = props.row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const currentValue = useMemo<ColumnValue<Milestone>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      return <MilestoneEditor currentValue={currentValue} model={item} rowIndex={props.row.index} columnId={columnId} />
    },
  },
  labels: {
    Placeholder: <LoadingLabelsCell />,
    Cell(props) {
      const columnId = SystemColumnId.Labels

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<Label>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <LabelsRenderer
          currentValue={currentValue}
          model={props.row.original}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original

      const columnId = SystemColumnId.Labels

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<Label>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      return <LabelsEditor currentValue={currentValue} model={item} rowIndex={props.row.index} columnId={columnId} />
    },
  },
  repository: {
    Placeholder: <LoadingRepositoryCell />,
    Cell(props) {
      const columnId = SystemColumnId.Repository

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<ExtendedRepository>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <RepositoryRenderer
          currentValue={currentValue}
          dropdownRef={props.dropdownRef}
          model={props.row.original}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original
      const columnId = SystemColumnId.Repository
      if (item.contentType === ItemType.DraftIssue) {
        return <RepositoryEditor model={item} rowIndex={props.row.index} columnId={columnId} />
      }
      return null
    },
  },
  linkedPullRequests: {
    Placeholder: <LoadingLinkedPullRequestsCell />,
    Cell(props) {
      const columnId = SystemColumnId.LinkedPullRequests

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<LinkedPullRequest>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <LinkedPullRequestsRenderer model={props.row.original} currentValue={currentValue} />
    },
  },
  reviewers: {
    Placeholder: <LoadingReviewersCell />,
    Cell(props) {
      const columnId = SystemColumnId.Reviewers

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<UserGroupItem>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          const users = columnData.map(itemFromReview)
          return withValue(users)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <ReviewersRenderer model={props.row.original} currentValue={currentValue} />
    },
  },
  issueType: {
    Placeholder: <LoadingIssueTypeCell />,
    Cell(props) {
      const columnId = SystemColumnId.IssueType

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<IssueType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <IssueTypeRenderer
          model={props.row.original}
          currentValue={currentValue}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original

      const columnId = SystemColumnId.IssueType

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<IssueType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.DraftIssue || item.contentType === ItemType.Issue) {
        return (
          <IssueTypeEditor currentValue={currentValue} model={item} rowIndex={props.row.index} columnId={columnId} />
        )
      }
      return null
    },
  },
  parentIssue: {
    Placeholder: <LoadingParentIssueCell />,
    Cell(props) {
      const columnId = SystemColumnId.ParentIssue

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<ParentIssue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <ParentIssueRenderer
          model={props.row.original}
          currentValue={currentValue}
          dropdownRef={props.dropdownRef}
          isDisabled={props.isDisabled}
        />
      )
    },
    CellEditor(props) {
      const item = props.row.original

      const columnId = SystemColumnId.ParentIssue

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<ParentIssue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType !== ItemType.Issue) return null

      return (
        <ParentIssueEditor currentValue={currentValue} model={item} rowIndex={props.row.index} columnId={columnId} />
      )
    },
  },
  subIssuesProgress: {
    Placeholder: <LoadingSubIssuesProgressCell />,
    Cell(props) {
      const columnId = SystemColumnId.SubIssuesProgress

      const isLoaded = itemHasColumnLoaded(props.row.original, columnId)

      const columnData = props.row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<SubIssuesProgress>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <SubIssuesProgressRenderer model={props.row.original} currentValue={currentValue} />
    },
  },
}

export function getColumnMeta(column: ColumnModel): Omit<PartialCustomColumnMeta, 'nullWhenRedacted'> {
  const {nullWhenRedacted, ...columnMeta} = columnMetaByFieldDataType[column.dataType]
  if (nullWhenRedacted === false) {
    return columnMeta
  }
  return {
    ...columnMeta,
    Cell: props => {
      if (props.row.original.contentType === ItemType.RedactedItem) {
        return null
      }
      return columnMeta.Cell(props)
    },
  }
}
